import { FileText, X, Square, ArrowUp, RotateCcw, Scissors, ArrowRightLeft, Briefcase, Zap, MessageSquare } from 'lucide-react'
import { DragEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from '@tanstack/react-router'
import { useMessage, type MessageContext } from '../../hooks/useMessage'
import fileDiscoveryService, { FileDiscoveryItem } from '../../services/fileDiscoveryService'
import { useConversationStore } from '../../stores/conversationStore'
import { useVideoWorkspaceStore } from '../../stores/videoWorkspaceStore'
import { useSettingsStore } from '../../stores/settingsStore'
import { useMessageStore } from '../../stores/messageStore'
import { useMessageInputStore } from '../../stores/messageInputStore'
import { AutoResizeTextarea } from '../ui/auto-resize-textarea'
import { FileAutoComplete, FileAutoCompleteRef } from './FileAutoComplete'
import { LayoutAutoComplete, LayoutAutoCompleteRef } from './LayoutAutoComplete'
import layoutMentionService, { LayoutMentionItem } from '../../services/layoutMentionService'
import { UploadModal } from './UploadModal'
import { ComposerActions } from './ComposerActions'
import { ResetConfirmationDialog } from './ResetConfirmationDialog'
import { ThemePaletteSelector, ThemePalette } from './ThemePaletteSelector'
import { ThemePreviewDisplay } from './ThemePreviewDisplay'
import { conversationAPI } from '../../services/api'
import { cn } from '../../lib/utils'
import { AIEditQueueItem } from '../slides/PropertyPanel'
import { AIEditQueueDisplay } from './AIEditQueueDisplay'
import { OutlineChangeQueueDisplay } from './OutlineChangeQueueDisplay'
import { ProviderSelector } from './ProviderSelector'
import type { ProviderType } from '../../services/messageService'
import type { OutlineChangeQueueItem } from '../../types/outlineChangeQueue'

// Supported file formats (same as CLI)
const SUPPORTED_IMAGE_FORMATS = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp']
const SUPPORTED_PDF_FORMATS = ['application/pdf']
const SUPPORTED_FILE_FORMATS = [...SUPPORTED_IMAGE_FORMATS, ...SUPPORTED_PDF_FORMATS]
const MAX_TOTAL_SIZE = 100 * 1024 * 1024 // 100MB

interface AttachedFile {
  id: string
  file: File
  data: ArrayBuffer
  mimeType: string
  preview?: string
  type: 'image' | 'pdf'
}

interface FileReference {
  id: string
  path: string
  name: string
  content?: string
  type: 'file' | 'directory'
  extension?: string
  size?: number
}

export function MessageInput() {
  const location = useLocation()
  const { activeConversationId, conversations } = useConversationStore()
  const { sendMessage, isStreaming, interruptMessage } = useMessage()
  const { currentModel, setCurrentModel } = useSettingsStore()
  const messageStore = useMessageStore()
  const { pendingMentions, clearMentions } = useMessageInputStore()

  // Get outline and active chat context from video workspace store for context detection
  const outline = useVideoWorkspaceStore((state) => state.outline)
  const activeChatContext = useVideoWorkspaceStore((state) => state.activeChatContext)

  // Outline change queue from video workspace store
  const outlineChangeQueue = useVideoWorkspaceStore((state) =>
    activeConversationId ? (state.outlineChangeQueueByConversation[activeConversationId] || []) : []
  )
  const removeOutlineChange = useVideoWorkspaceStore((state) => state.removeOutlineChange)
  const clearOutlineChangeQueue = useVideoWorkspaceStore((state) => state.clearOutlineChangeQueue)

  // Get current conversation for type checking
  const currentConversation = conversations.find(c => c.id === activeConversationId)
  const effectiveConversation = currentConversation

  // Determine if we're in video workspace (for context-based routing)
  const isVideoWorkspace = location.pathname.includes('/video-workspace')

  // Build message context for VIDEO conversations
  const buildMessageContext = useCallback((): MessageContext | undefined => {
    // Only add context for VIDEO conversations in video workspace
    if (!isVideoWorkspace || effectiveConversation?.collectionType !== 'VIDEO') {
      return undefined
    }

    // Check if we're in narration context
    if (activeChatContext === 'narration') {
      return {
        source: 'narration',
        narrationAction: 'edit', // Always edit mode for chat-based script editing
      }
    }

    // Determine action based on outline existence
    // No outline = create mode, has outline = edit mode
    const outlineAction: 'create' | 'edit' = outline ? 'edit' : 'create'

    return {
      source: 'outline',
      outlineAction,
    }
  }, [isVideoWorkspace, effectiveConversation?.collectionType, outline, activeChatContext])
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [message, setMessage] = useState('')
  const [isComposing, setIsComposing] = useState(false)
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [fileReferences, setFileReferences] = useState<FileReference[]>([])
  const [showFileAutoComplete, setShowFileAutoComplete] = useState(false)
  const [showLayoutAutoComplete, setShowLayoutAutoComplete] = useState(false)
  const [autoCompletePosition, setAutoCompletePosition] = useState({ top: 0, left: 0, showAbove: false })
  const [layoutMentions, setLayoutMentions] = useState<LayoutMentionItem[]>([])
  const [showResetDialog, setShowResetDialog] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [aiEditQueue, setAiEditQueue] = useState<AIEditQueueItem[]>([])
  const [isProcessingAI, setIsProcessingAI] = useState(false)
  const [selectedTheme, setSelectedTheme] = useState<ThemePalette | null>(null)
  const [selectedProvider, setSelectedProvider] = useState<ProviderType>('gemini')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const fileAutoCompleteRef = useRef<FileAutoCompleteRef>(null)
  const layoutAutoCompleteRef = useRef<LayoutAutoCompleteRef>(null)

  const isCompact = false // Can be made dynamic based on viewport or route

  // Narration quick action definitions
  const narrationQuickActions = [
    { id: 'shorten', label: 'Shorten', icon: Scissors, prompt: 'Shorten all scripts by 20% while keeping key information' },
    { id: 'transitions', label: 'Transitions', icon: ArrowRightLeft, prompt: 'Add smooth transitions between slides for better flow' },
    { id: 'professional', label: 'Professional', icon: Briefcase, prompt: 'Make the tone more professional and polished' },
    { id: 'energetic', label: 'Energetic', icon: Zap, prompt: 'Make scripts more energetic and engaging' },
    { id: 'simplify', label: 'Simplify', icon: MessageSquare, prompt: 'Simplify language for a general audience' },
  ]

  // Check if we should show narration quick actions
  const showNarrationQuickActions = activeChatContext === 'narration' && isVideoWorkspace && !isStreaming && activeConversationId

  // Handle quick action click - sets message and optionally auto-sends
  const handleQuickActionClick = useCallback((prompt: string) => {
    setMessage(prompt)
    textareaRef.current?.focus()
  }, [])

  // Listen for AI queue sync events (real-time updates from slide editor)
  useEffect(() => {
    const handleAIQueueSync = (event: CustomEvent) => {
      const { queue, conversationId: eventConversationId } = event.detail;

      // Only update if the event is for the active conversation or no conversation specified
      if (eventConversationId === activeConversationId || !eventConversationId) {
        console.log('🔄 AI queue synced:', queue?.length || 0, 'items');
        setAiEditQueue(queue || []);
      }
    };

    const handleAIQueueClear = (event: CustomEvent) => {
      const { conversationId: eventConversationId } = event.detail;
      if (eventConversationId === activeConversationId || !eventConversationId) {
        console.log('🧹 AI queue cleared');
        setAiEditQueue([]);
        setIsProcessingAI(false);
      }
    };

    window.addEventListener('aiQueueSync', handleAIQueueSync as EventListener);
    window.addEventListener('aiQueueClear', handleAIQueueClear as EventListener);

    return () => {
      window.removeEventListener('aiQueueSync', handleAIQueueSync as EventListener);
      window.removeEventListener('aiQueueClear', handleAIQueueClear as EventListener);
    };
  }, [activeConversationId]);

  // Listen for AI edits ready events - auto-send with summary
  useEffect(() => {
    const handleAIEditsReady = async (event: CustomEvent) => {
      const { fullMarkdown, summaryText, queue } = event.detail;

      console.log('🤖 AI edits ready to send:', queue?.length || 0, 'items');

      if (!activeConversationId || !fullMarkdown) {
        console.warn('Cannot send AI edits: missing conversation or content');
        return;
      }

      setIsProcessingAI(true);

      try {
        // Encode queue data as JSON marker for MessageBubble to render as cards
        const queueData = JSON.stringify((queue || []).map((item: AIEditQueueItem) => ({
          id: item.id,
          elementName: item.elementName,
          instruction: item.instruction
        })));

        // Prepend queue data marker to message content
        const messageContent = `[AI_EDIT_QUEUE]${queueData}[/AI_EDIT_QUEUE]\n\n${fullMarkdown}`;

        // Send the message with embedded queue data
        await sendMessage({
          conversationId: activeConversationId,
          content: messageContent,
          model: currentModel,
          context: buildMessageContext(),
        });

        // Clear the queue after successful send
        setAiEditQueue([]);

        // Show success toast with summary
        window.dispatchEvent(new CustomEvent('showToast', {
          detail: {
            message: summaryText || 'AI edits sent successfully',
            type: 'success'
          }
        }));
      } catch (error) {
        console.error('Failed to send AI edits:', error);
        window.dispatchEvent(new CustomEvent('showToast', {
          detail: {
            message: 'Failed to send AI edits',
            type: 'error'
          }
        }));
      } finally {
        setIsProcessingAI(false);
      }
    };

    window.addEventListener('aiEditsReady', handleAIEditsReady as unknown as EventListener);
    return () => {
      window.removeEventListener('aiEditsReady', handleAIEditsReady as unknown as EventListener);
    };
  }, [activeConversationId, sendMessage, currentModel, buildMessageContext]);

  // Listen for slide edit request events
  useEffect(() => {
    const handleSlideEditRequest = (event: CustomEvent) => {
      const { slideId, sceneNumber } = event.detail;

      console.log('✏️ Received slide edit request:', { slideId, sceneNumber });

      // Set the message to the edit prompt
      const editPrompt = `Edit Scene ${sceneNumber}: `;
      setMessage(editPrompt);

      // Focus the textarea at the end of the text
      if (textareaRef.current) {
        textareaRef.current.focus();
        // Move cursor to end
        const length = editPrompt.length;
        textareaRef.current.setSelectionRange(length, length);
      }
    };

    window.addEventListener('slideEditRequest', handleSlideEditRequest as EventListener);
    return () => {
      window.removeEventListener('slideEditRequest', handleSlideEditRequest as EventListener);
    };
  }, []);

  // File handling functions
  const validateFile = (file: File): string | null => {
    if (!SUPPORTED_FILE_FORMATS.includes(file.type)) {
      return `Unsupported format: ${file.type}. Supported: PNG, JPG, GIF, WEBP, SVG, BMP, PDF`
    }

    const totalSize = attachedFiles.reduce((sum, f) => sum + f.file.size, 0) + file.size
    if (totalSize > MAX_TOTAL_SIZE) {
      return `Total size would exceed 100MB limit`
    }

    return null
  }

  const processFile = async (file: File): Promise<AttachedFile> => {
    const arrayBuffer = await file.arrayBuffer()
    const isImage = SUPPORTED_IMAGE_FORMATS.includes(file.type)

    return {
      id: Math.random().toString(36).substr(2, 9),
      file,
      data: arrayBuffer,
      mimeType: file.type,
      preview: isImage ? URL.createObjectURL(file) : undefined,
      type: isImage ? 'image' : 'pdf'
    }
  }

  // Helper function to convert ArrayBuffer to base64
  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    const chunkSize = 0x8000 // 32KB chunks to avoid stack overflow

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length))
      binary += String.fromCharCode(...chunk)
    }

    return btoa(binary)
  }

  const addFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files)
    const validFiles: File[] = []
    const errors: string[] = []

    for (const file of fileArray) {
      const error = validateFile(file)
      if (error) {
        errors.push(`${file.name}: ${error}`)
      } else {
        validFiles.push(file)
      }
    }

    if (errors.length > 0) {
      alert(`Some files could not be added:\n${errors.join('\n')}`)
    }

    if (validFiles.length > 0) {
      const processedFiles = await Promise.all(validFiles.map(processFile))
      setAttachedFiles(prev => [...prev, ...processedFiles])
    }
  }, [attachedFiles])

  const removeFile = (id: string) => {
    setAttachedFiles(prev => {
      const filtered = prev.filter(f => f.id !== id)
      // Revoke URL to prevent memory leaks
      const removed = prev.find(f => f.id === id)
      if (removed && removed.preview) {
        URL.revokeObjectURL(removed.preview)
      }
      return filtered
    })
  }

  const handleSend = async () => {
    if ((message.trim() || attachedFiles.length > 0 || fileReferences.length > 0 || selectedTheme || outlineChangeQueue.length > 0) && !isStreaming && activeConversationId) {
      try {
        // Check if conversation is locked (another job is processing)
        // This prevents duplicate messages after page refresh
        try {
          const lockStatus = await conversationAPI.getLockStatus(activeConversationId);
          if (lockStatus.locked) {
            console.warn('Conversation is locked, blocking send:', lockStatus);
            // Set streaming state to show "AI is responding..."
            messageStore.setStreamingMessage(lockStatus.messageId || 'locked');
            // Show user-friendly message
            const duration = lockStatus.lockedDuration
              ? `(running for ${Math.round(lockStatus.lockedDuration / 60)} min)`
              : '';
            alert(`Please wait - a message is being processed ${duration}. The page will show the response when it completes.`);
            return;
          }
        } catch (lockError) {
          // If lock check fails, proceed anyway (don't block user)
          console.warn('Failed to check lock status:', lockError);
        }

        // Build theme message prefix if a theme is selected
        let themePrefix = ''
        if (selectedTheme) {
          // Encode theme data as JSON marker for MessageBubble to render as visual block
          const themeData = JSON.stringify({
            id: selectedTheme.id,
            name: selectedTheme.name,
            colors: selectedTheme.colors
          });
          themePrefix = `[THEME_PALETTE]${themeData}[/THEME_PALETTE]\n\n`
        }

        // Process @ file references and replace them with file content
        let processedMessage = message.trim()
        const fileContents: string[] = []

        // Load content for all file references
        for (const fileRef of fileReferences) {
          if (fileRef.content) {
            fileContents.push(`\n--- Content from @${fileRef.path} ---\n${fileRef.content}\n--- End of ${fileRef.name} ---\n`)
          }
        }

        // Append file contents to message
        if (fileContents.length > 0) {
          processedMessage += '\n\n--- Referenced Files ---' + fileContents.join('') + '--- End of References ---'
        }

        // Process layout mentions and attach their files
        const layoutFileData: Array<{ data: string, mimeType: string, filename: string }> = []

        for (const layoutMention of layoutMentions) {
          try {
            const layoutFiles = await layoutMentionService.getLayoutFiles(layoutMention.id, activeConversationId)

            // Add HTML file
            if (layoutFiles.htmlFile) {
              const htmlArrayBuffer = await layoutFiles.htmlFile.arrayBuffer()
              layoutFileData.push({
                data: arrayBufferToBase64(htmlArrayBuffer),
                mimeType: layoutFiles.htmlFile.type,
                filename: layoutFiles.htmlFile.name
              })
            }

            // Add reference image
            if (layoutFiles.imageFile) {
              const imageArrayBuffer = await layoutFiles.imageFile.arrayBuffer()
              layoutFileData.push({
                data: arrayBufferToBase64(imageArrayBuffer),
                mimeType: layoutFiles.imageFile.type,
                filename: layoutFiles.imageFile.name
              })
            }
          } catch (error) {
            console.error(`Error processing layout mention ${layoutMention.id}:`, error)
          }
        }

        // Prepare file data for web-core from direct attachments
        const attachedFileData = attachedFiles.map(f => ({
          data: arrayBufferToBase64(f.data),
          mimeType: f.mimeType,
          filename: f.file.name
        }))

        // Extract images from recent tool executions (for follow-up messages after read_file)
        const toolImageData: Array<{ data: string, mimeType: string, filename?: string }> = []
        if (activeConversationId) {
          const messages = messageStore.getMessagesForConversation(activeConversationId)
          // Look at the most recent assistant message for tool executions with images
          const recentAssistantMessages = messages.filter(m => m.role === 'assistant').slice(-2) // Last 2 assistant messages

          for (const msg of recentAssistantMessages) {
            const toolExecutions = messageStore.getToolExecutionsForMessage(msg.id)
            for (const execution of toolExecutions) {
              if (execution.images && execution.images.length > 0) {
                toolImageData.push(...execution.images)
              }
            }
          }
        }

        // Combine attached files, layout files, and tool execution images
        const allImageData = [...attachedFileData, ...layoutFileData, ...toolImageData]

        // Build final content with theme prefix and markdown instructions
        let finalContent = ''

        if (selectedTheme) {
          // Add theme marker for visual rendering in chat
          finalContent += themePrefix

          // Add markdown instructions for the AI (hidden from chat display)
          const themeInstructions = `Apply this color theme to all slides:

**Theme: ${selectedTheme.name}**
- Primary: ${selectedTheme.colors.primary}
- Secondary: ${selectedTheme.colors.secondary}
- Accent: ${selectedTheme.colors.accent}
- Background: ${selectedTheme.colors.background}
- Text: ${selectedTheme.colors.text}

Update the slide colors to match this palette while maintaining the overall design.`

          // Wrap AI instructions in hidden marker
          finalContent += `[THEME_INSTRUCTIONS]${themeInstructions}[/THEME_INSTRUCTIONS]`

          // Add any additional user message (this will be visible)
          if (processedMessage) {
            finalContent += '\n\n' + processedMessage
          }
        } else {
          // No theme - just use processed message
          finalContent = processedMessage || (allImageData.length > 0 ? 'Please analyze the attached files.' : '')
        }

        // Merge outline change queue into message if items are queued
        const hasQueuedChanges = outlineChangeQueue.length > 0
        if (hasQueuedChanges) {
          // Encode queue data as JSON marker for MessageBubble to render as cards
          const queueData = JSON.stringify(outlineChangeQueue.map((item: OutlineChangeQueueItem) => ({
            id: item.id,
            slideId: item.slideId,
            slideTitle: item.slideTitle,
            changeType: item.changeType,
            description: item.description,
          })))

          // Build markdown instructions for the AI per change type
          const changeLines = outlineChangeQueue.map((item) => {
            const d = item.details
            if (item.changeType === 'add_slide') {
              const afterLabel = d.afterSlideId ? ` (after ${d.afterSlideId})` : ''
              const layoutLabel = d.layoutName ? `\nLayout: ${d.layoutName} (${d.layoutId})` : '\nLayout: AI picks best layout'
              return `### New Slide${afterLabel}\n**Add:** ${d.instructions}${layoutLabel}\n`
            }
            if (item.changeType === 'delete_slide') {
              return `### Slide "${item.slideTitle}" (${item.slideId})\n**Delete:** Remove this slide from the outline.\n`
            }
            // layout_change
            return `### Slide "${item.slideTitle}" (${item.slideId})\n**Change:** Layout changed from "${d.oldDisplayName}" (${d.oldValue}) to "${d.newDisplayName}" (${d.newValue})\n`
          }).join('\n')

          const queueContent = `[OUTLINE_CHANGE_QUEUE]${queueData}[/OUTLINE_CHANGE_QUEUE]\n\n## Outline Update Request\n\n**Changes:** ${outlineChangeQueue.length} outline change${outlineChangeQueue.length !== 1 ? 's' : ''}\n\n${changeLines}\nFor layout changes, update each slide's description, key points, visual instructions, and audience takeaway to work optimally with the new layout.\nFor new slides, generate full content (title, description, key points, visual instructions, audience takeaway) based on the instructions.\nFor deletions, remove the slide from the outline and renumber remaining slides.\n\n---\n**Apply these changes to the outline and return the updated plan.**`

          // Combine: queue content first, then any user text
          if (finalContent) {
            finalContent = queueContent + '\n\n' + finalContent
          } else {
            finalContent = queueContent
          }
        }

        // Send message without creating placeholder here - messageService will handle it
        // This prevents duplicate placeholder creation
        // When queue items are present, force outline edit context
        const messageContext = hasQueuedChanges
          ? { source: 'outline' as const, outlineAction: 'edit' as const }
          : buildMessageContext()

        await sendMessage({
          conversationId: activeConversationId,
          content: finalContent,
          images: allImageData.length > 0 ? allImageData : undefined,
          model: currentModel,
          provider: selectedProvider, // AI provider: 'gemini' | 'claude'
          context: messageContext, // UI context for VIDEO workflow routing
          // Don't pass placeholderMessageId - let messageService create it to avoid duplicates
        })

        // Clear input, files, file references, layout mentions, selected theme, and outline queue
        setMessage('')
        setFileReferences([])
        setLayoutMentions([])
        setSelectedTheme(null)
        if (hasQueuedChanges) {
          clearOutlineChangeQueue(activeConversationId)
        }
        setAttachedFiles(prev => {
          prev.forEach(f => f.preview && URL.revokeObjectURL(f.preview))
          return []
        })
        textareaRef.current?.focus()
      } catch (error) {
        console.error('Failed to send message:', error)
      }
    }
  }

  // Handle cancel/interrupt message
  const handleCancel = async () => {
    if (isStreaming && activeConversationId) {
      try {
        await interruptMessage(activeConversationId)
      } catch (error) {
        console.error('Failed to cancel message:', error)
      }
    }
  }

  const handleResetConversation = async () => {
    if (!activeConversationId) return

    setIsResetting(true)
    try {
      await conversationAPI.resetCheckpoint(activeConversationId)

      // Show success toast
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: 'Conversation reset successfully',
          type: 'success'
        }
      }))

      setShowResetDialog(false)
    } catch (error) {
      console.error('Failed to reset conversation:', error)

      // Show error toast
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: 'Failed to reset conversation',
          type: 'error'
        }
      }))
    } finally {
      setIsResetting(false)
    }
  }

  // Drag and drop handlers
  const handleDragOver = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    // Check if this is a resource mention drop from ResourcesTab
    const resourceMention = e.dataTransfer.getData('resource/mention') || e.dataTransfer.getData('text/plain')
    if (resourceMention && resourceMention.startsWith('@resources/')) {
      // Insert the @mention at cursor position or append to message
      if (textareaRef.current) {
        const textarea = textareaRef.current
        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const currentMessage = message

        // Insert mention at cursor position
        const newMessage = currentMessage.substring(0, start) + resourceMention + ' ' + currentMessage.substring(end)
        setMessage(newMessage)

        // Set cursor position after the inserted mention
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + resourceMention.length + 1
          textarea.focus()
        }, 0)
      } else {
        // Fallback: append to message
        setMessage(prev => prev + (prev ? ' ' : '') + resourceMention + ' ')
      }
      return
    }

    // Handle regular file drops
    const files = e.dataTransfer.files
    if (files.length > 0) {
      addFiles(files)
    }
  }

  // File input handler
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      addFiles(files)
      // Reset input value to allow selecting same file again
      e.target.value = ''
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Handle layout auto-complete navigation (priority for style conversations)
    if (showLayoutAutoComplete && layoutAutoCompleteRef.current?.isVisible()) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          layoutAutoCompleteRef.current.selectNext()
          return
        case 'ArrowUp':
          e.preventDefault()
          layoutAutoCompleteRef.current.selectPrevious()
          return
        case 'Enter':
          if (!e.shiftKey) {
            e.preventDefault()
            layoutAutoCompleteRef.current.selectCurrent()
            return
          }
          break
        case 'Escape':
          e.preventDefault()
          setShowLayoutAutoComplete(false)
          return
        case 'Tab':
          e.preventDefault()
          layoutAutoCompleteRef.current.selectCurrent()
          return
      }
    }

    // Handle file auto-complete navigation
    if (showFileAutoComplete && fileAutoCompleteRef.current?.isVisible()) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          fileAutoCompleteRef.current.selectNext()
          return
        case 'ArrowUp':
          e.preventDefault()
          fileAutoCompleteRef.current.selectPrevious()
          return
        case 'Enter':
          if (!e.shiftKey) {
            e.preventDefault()
            fileAutoCompleteRef.current.selectCurrent()
            return
          }
          break
        case 'Escape':
          e.preventDefault()
          setShowFileAutoComplete(false)
          return
        case 'Tab':
          e.preventDefault()
          fileAutoCompleteRef.current.selectCurrent()
          return
      }
    }

    // Handle ESC key to cancel streaming
    if (e.key === 'Escape' && isStreaming && activeConversationId) {
      e.preventDefault()
      handleCancel()
      return
    }

    // Regular message handling
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleCompositionStart = () => {
    setIsComposing(true)
  }

  const handleCompositionEnd = () => {
    setIsComposing(false)
  }

  // Calculate auto-complete position
  const calculateAutoCompletePosition = useCallback(() => {
    if (!textareaRef.current) return null

    const rect = textareaRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    const dropdownHeight = 256 // max-h-64 = 16rem = 256px
    const dropdownWidth = 320 // maxWidth from component
    const margin = 8

    const spaceBelow = viewportHeight - rect.bottom
    const spaceAbove = rect.top

    const shouldShowAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow

    let top: number
    if (shouldShowAbove) {
      top = rect.top - dropdownHeight - margin
    } else {
      top = rect.bottom + margin
    }

    const left = Math.max(margin, Math.min(rect.left, viewportWidth - dropdownWidth - margin))

    return { top, left, showAbove: shouldShowAbove }
  }, [])

  // Handle message input changes and @ symbol detection
  const handleMessageChange = async (value: string) => {
    setMessage(value)

    const fileParsed = fileDiscoveryService.parseAtCommand(value)
    const layoutParsed = layoutMentionService.parseAtCommand(value)

    // Prioritize layout mentions for style conversations
    if (layoutParsed && effectiveConversation?.collectionType === 'STYLE') {
      const position = calculateAutoCompletePosition()
      if (position) {
        setAutoCompletePosition(position)
        setShowLayoutAutoComplete(true)
        setShowFileAutoComplete(false)
      }
    } else if (fileParsed) {
      const position = calculateAutoCompletePosition()
      if (position) {
        setAutoCompletePosition(position)
        setShowFileAutoComplete(true)
        setShowLayoutAutoComplete(false)
      }
    } else {
      setShowFileAutoComplete(false)
      setShowLayoutAutoComplete(false)
    }
  }

  // Handle file selection from auto-complete
  const handleFileSelect = async (file: FileDiscoveryItem) => {
    const parsed = fileDiscoveryService.parseAtCommand(message)
    if (!parsed) return

    const newAtCommand = `@${file.path}`
    const newMessage = parsed.prefix + newAtCommand + parsed.suffix
    setMessage(newMessage)
    setShowFileAutoComplete(false)

    if (file.type === 'file') {
      try {
        const content = await fileDiscoveryService.getFileContent(file.path, activeConversationId || undefined)
        if (content) {
          const fileRef: FileReference = {
            id: Math.random().toString(36).substr(2, 9),
            path: file.path,
            name: file.name,
            content: content.content,
            type: file.type,
            extension: file.extension,
            size: file.size
          }
          setFileReferences(prev => {
            const filtered = prev.filter(ref => ref.path !== file.path)
            return [...filtered, fileRef]
          })
        }
      } catch (error) {
        console.error('Error loading file content:', error)
      }
    }

    textareaRef.current?.focus()
  }

  // Handle layout selection from auto-complete
  const handleLayoutSelect = async (layout: LayoutMentionItem) => {
    const parsed = layoutMentionService.parseAtCommand(message)
    if (!parsed) return

    const newAtCommand = `@${layout.id}`
    const newMessage = parsed.prefix + newAtCommand + parsed.suffix
    setMessage(newMessage)
    setShowLayoutAutoComplete(false)

    setLayoutMentions(prev => {
      const filtered = prev.filter(mention => mention.id !== layout.id)
      return [...filtered, layout]
    })

    textareaRef.current?.focus()
  }

  // Remove file reference
  const removeFileReference = (id: string) => {
    setFileReferences(prev => prev.filter(ref => ref.id !== id))

    const ref = fileReferences.find(r => r.id === id)
    if (ref) {
      const atCommand = `@${ref.path}`
      const newMessage = message.replace(atCommand, '')
      setMessage(newMessage.trim())
    }
  }

  // AI Edit Queue handlers
  const handleRemoveFromAIQueue = useCallback((id: string) => {
    console.log('🗑️ Removing from AI queue:', id);
    // Dispatch event to GsapSlidePreviewEnhanced to update its state
    window.dispatchEvent(new CustomEvent('aiEditRemoved', { detail: { id } }));
    // Update local state immediately for responsive UI
    setAiEditQueue(prev => prev.filter(item => item.id !== id));
  }, []);

  const handleSendAllFromQueue = useCallback(() => {
    if (aiEditQueue.length === 0) return;
    console.log('📤 Requesting to send all AI edits from queue');
    // Dispatch event to GsapSlidePreviewEnhanced to trigger send
    window.dispatchEvent(new CustomEvent('requestSendAIEdits', {
      detail: { conversationId: activeConversationId }
    }));
  }, [aiEditQueue.length, activeConversationId]);

  // Outline change queue handlers
  const handleRemoveOutlineChange = useCallback((id: string) => {
    if (!activeConversationId) return
    removeOutlineChange(activeConversationId, id)
  }, [activeConversationId, removeOutlineChange])

  const [isProcessingOutlineChanges, setIsProcessingOutlineChanges] = useState(false)

  const handleSendOutlineChanges = useCallback(async () => {
    if (!activeConversationId || outlineChangeQueue.length === 0) return

    setIsProcessingOutlineChanges(true)

    try {
      // Encode queue data as JSON marker for MessageBubble to render as cards
      const queueData = JSON.stringify(outlineChangeQueue.map((item: OutlineChangeQueueItem) => ({
        id: item.id,
        slideId: item.slideId,
        slideTitle: item.slideTitle,
        changeType: item.changeType,
        description: item.description,
      })))

      // Build markdown instructions for the AI per change type
      const changeLines = outlineChangeQueue.map((item) => {
        const d = item.details
        if (item.changeType === 'add_slide') {
          const afterLabel = d.afterSlideId ? ` (after ${d.afterSlideId})` : ''
          const layoutLabel = d.layoutName ? `\nLayout: ${d.layoutName} (${d.layoutId})` : '\nLayout: AI picks best layout'
          return `### New Slide${afterLabel}\n**Add:** ${d.instructions}${layoutLabel}\n`
        }
        if (item.changeType === 'delete_slide') {
          return `### Slide "${item.slideTitle}" (${item.slideId})\n**Delete:** Remove this slide from the outline.\n`
        }
        // layout_change
        return `### Slide "${item.slideTitle}" (${item.slideId})\n**Change:** Layout changed from "${d.oldDisplayName}" (${d.oldValue}) to "${d.newDisplayName}" (${d.newValue})\n`
      }).join('\n')

      const messageContent = `[OUTLINE_CHANGE_QUEUE]${queueData}[/OUTLINE_CHANGE_QUEUE]\n\n## Outline Update Request\n\n**Changes:** ${outlineChangeQueue.length} outline change${outlineChangeQueue.length !== 1 ? 's' : ''}\n\n${changeLines}\nFor layout changes, update each slide's description, key points, visual instructions, and audience takeaway to work optimally with the new layout.\nFor new slides, generate full content (title, description, key points, visual instructions, audience takeaway) based on the instructions.\nFor deletions, remove the slide from the outline and renumber remaining slides.\n\n---\n**Apply these changes to the outline and return the updated plan.**`

      await sendMessage({
        conversationId: activeConversationId,
        content: messageContent,
        model: currentModel,
        context: { source: 'outline', outlineAction: 'edit' },
      })

      // Clear the queue after successful send
      clearOutlineChangeQueue(activeConversationId)
    } catch (error) {
      console.error('Failed to send outline changes:', error)
    } finally {
      setIsProcessingOutlineChanges(false)
    }
  }, [activeConversationId, outlineChangeQueue, sendMessage, currentModel, clearOutlineChangeQueue])

  // Handle theme palette selection - stores in state for user to send
  const handleThemePaletteSelect = useCallback((palette: ThemePalette) => {
    setSelectedTheme(palette);
    // Focus the textarea so user can add instructions
    textareaRef.current?.focus();
  }, []);

  // Remove selected theme
  const handleRemoveTheme = useCallback(() => {
    setSelectedTheme(null);
  }, []);

  // Handle window resize and scroll to reposition dropdown
  useEffect(() => {
    if (!showFileAutoComplete && !showLayoutAutoComplete) return

    const handleReposition = () => {
      const position = calculateAutoCompletePosition()
      if (position) {
        setAutoCompletePosition(position)
      }
    }

    const handleScroll = () => {
      setShowFileAutoComplete(false)
      setShowLayoutAutoComplete(false)
    }

    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleScroll, true)

    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [showFileAutoComplete, showLayoutAutoComplete, calculateAutoCompletePosition])

  // Check for pending mentions from ResourcesTab
  useEffect(() => {
    if (pendingMentions.length > 0 && activeConversationId) {
      // Add pending mentions to the message
      const mentionsText = pendingMentions.join(' ') + ' '
      setMessage(prev => prev + (prev ? ' ' : '') + mentionsText)

      // Clear the pending mentions
      clearMentions()

      // Focus the textarea
      textareaRef.current?.focus()
    }
  }, [pendingMentions, activeConversationId, clearMentions])

  const hasContent = message.trim() || attachedFiles.length > 0 || fileReferences.length > 0 || selectedTheme || outlineChangeQueue.length > 0

  return (
    <div className={cn(
      "w-full mx-auto",
      isCompact ? "max-w-full" : "max-w-3xl"
    )}>
      {/* File References */}
      {fileReferences.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-2">
            {fileReferences.map((fileRef) => (
              <div key={fileRef.id} className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-full text-xs group">
                <FileText className="h-3 w-3 text-muted-foreground" />
                <span className="text-foreground/80 max-w-[200px] truncate">{fileRef.name}</span>
                <button
                  onClick={() => removeFileReference(fileRef.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* File Previews */}
      {attachedFiles.length > 0 && (
        <div className="mb-3">
          <div className="flex flex-wrap gap-2">
            {attachedFiles.map((attachedFile) => (
              <div key={attachedFile.id} className="relative group">
                <div className="overflow-hidden rounded-lg border border-border">
                  {attachedFile.type === 'image' ? (
                    <img
                      src={attachedFile.preview}
                      alt={attachedFile.file.name}
                      className="h-14 w-14 object-cover"
                    />
                  ) : (
                    <div className="h-14 w-14 flex items-center justify-center bg-muted">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeFile(attachedFile.id)}
                  className="absolute -top-1 -right-1 h-5 w-5 bg-background border border-border rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Edit Queue Display - Shows queued edits from slide editor */}
      {aiEditQueue.length > 0 && (
        <AIEditQueueDisplay
          queue={aiEditQueue}
          onRemove={handleRemoveFromAIQueue}
          onSendAll={handleSendAllFromQueue}
          isProcessing={isProcessingAI}
          className="mb-3"
        />
      )}

      {/* Outline Change Queue Display - Shows queued layout changes */}
      {outlineChangeQueue.length > 0 && (
        <OutlineChangeQueueDisplay
          queue={outlineChangeQueue}
          onRemove={handleRemoveOutlineChange}
          onSendAll={handleSendOutlineChanges}
          isProcessing={isProcessingOutlineChanges}
          className="mb-3"
        />
      )}

      {/* Theme Preview Display - Shows selected theme before sending */}
      {selectedTheme && (
        <ThemePreviewDisplay
          theme={selectedTheme}
          onRemove={handleRemoveTheme}
          className="mb-3"
        />
      )}

      {/* Narration Quick Actions - Context-aware suggestion chips */}
      {showNarrationQuickActions && (
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Quick:</span>
          {narrationQuickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => handleQuickActionClick(action.prompt)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-muted/50 hover:bg-muted rounded-full text-muted-foreground hover:text-foreground transition-colors"
            >
              <action.icon className="h-3 w-3" />
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Modern Floating Input Container - Two Row Layout */}
      <div
        className={cn(
          "relative flex flex-col rounded-2xl floating-input transition-all duration-200 bg-muted/80 backdrop-blur-md border border-white/5",
          isDragOver ? "ring-primary/50 bg-primary/5" : "",
          isStreaming && "opacity-75"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Text Area Row */}
        <div className="px-3 pt-2.5 pb-1">
          <AutoResizeTextarea
            ref={textareaRef}
            data-message-input="true"
            value={message}
            onChange={(e) => handleMessageChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            placeholder={
              !activeConversationId
                ? "Select a conversation..."
                : isStreaming
                  ? "AI is responding..."
                  : "Message..."
            }
            disabled={isStreaming || !activeConversationId}
            minRows={1}
            maxRows={isCompact ? 5 : 8}
            className="w-full text-[15px] leading-relaxed bg-transparent resize-none focus:outline-none placeholder:text-muted-foreground/50"
          />
        </div>

        {/* Toolbar Row */}
        <div className="flex items-center justify-between px-2 pb-2 pt-0.5">
          {/* Left side actions */}
          <div className="flex items-center gap-1">
            {/* Plus Button with Actions */}
            <ComposerActions
              onUploadToResources={() => setShowUploadModal(true)}
              currentModel={currentModel}
              onModelChange={setCurrentModel}
              disabled={isStreaming || !activeConversationId}
              isCompact={isCompact}
            />

            {/* Theme Palette Selector - Show for SLIDE and VIDEO conversations, or in video workspace (child slide chats) */}
            {(effectiveConversation?.collectionType === 'SLIDE' || effectiveConversation?.collectionType === 'VIDEO' || isVideoWorkspace) && (
              <ThemePaletteSelector
                onApply={handleThemePaletteSelect}
                disabled={isStreaming || !activeConversationId}
              />
            )}

            {/* Reset Button */}
            {!isStreaming && activeConversationId && (
              <button
                onClick={() => setShowResetDialog(true)}
                className="flex items-center justify-center h-8 w-8 rounded-full hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all"
                title="Reset conversation context"
                aria-label="Reset conversation context"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Provider Selector - sleek pill toggle */}
            <ProviderSelector
              value={selectedProvider}
              onChange={setSelectedProvider}
              disabled={isStreaming || !activeConversationId}
            />

            {/* Send/Stop Button */}
            {isStreaming ? (
              <button
                onClick={handleCancel}
                className="flex items-center justify-center h-8 w-8 rounded-full bg-foreground text-background hover:opacity-90 transition-all"
                title="Stop generation (Esc)"
                aria-label="Stop generation"
              >
                <Square className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!hasContent || !activeConversationId}
                className={cn(
                  "flex items-center justify-center h-8 w-8 rounded-full transition-all",
                  hasContent && activeConversationId
                    ? "bg-foreground text-background hover:opacity-90"
                    : "bg-muted/50 text-muted-foreground cursor-not-allowed"
                )}
                title="Send message"
                aria-label="Send message"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={SUPPORTED_FILE_FORMATS.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>


      {/* File Auto-complete Dropdown */}
      <FileAutoComplete
        ref={fileAutoCompleteRef}
        inputValue={message}
        conversationId={activeConversationId || undefined}
        onFileSelect={handleFileSelect}
        onClose={() => setShowFileAutoComplete(false)}
        visible={showFileAutoComplete}
        position={autoCompletePosition}
      />

      {/* Layout Auto-complete Dropdown */}
      <LayoutAutoComplete
        ref={layoutAutoCompleteRef}
        inputValue={message}
        conversationId={activeConversationId || undefined}
        onLayoutSelect={handleLayoutSelect}
        onClose={() => setShowLayoutAutoComplete(false)}
        visible={showLayoutAutoComplete}
        position={autoCompletePosition}
      />

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        conversationId={activeConversationId || undefined}
      />

      {/* Reset Confirmation Dialog */}
      <ResetConfirmationDialog
        open={showResetDialog}
        onOpenChange={setShowResetDialog}
        onConfirm={handleResetConversation}
        isResetting={isResetting}
      />
    </div>
  )
}